// store/useAuthStore.ts
import { create } from 'zustand';
import { jwtDecode } from 'jwt-decode';
import toast from 'react-hot-toast';

import { api } from '@/lib/axios';
import { queryClient } from '@/lib/queryClient';
import { authRateLimiter } from '@/lib/rateLimiter';
import { logger } from '@/lib/logger';
import { API_ROUTES, STORAGE_KEYS, TOKEN_REFRESH_THRESHOLD_MS, AUTH_MESSAGES } from '@/constants';

import type {
  User,
  EffectiveRole,
  AuthResult,
  LoginRequest,
  LoginResponse,
  LoginErrorResponse,
  SignupRequest,
  ResetPasswordRequest,
  ResetPasswordConfirmRequest,
  ChangePasswordRequest,
  TokenResponse,
  ConfirmEmailResponse,
} from '@/types/auth';

// ============================================
// Helpers
// ============================================

const storage = {
  getAccessToken: () => localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
  getRefreshToken: () => localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
  getUser: (): User | null => {
    const data = localStorage.getItem(STORAGE_KEYS.USER);
    if (!data) return null;

    try {
      return JSON.parse(data) as User;
    } catch {
      localStorage.removeItem(STORAGE_KEYS.USER);
      return null;
    }
  },
  setTokens: (access: string, refresh: string) => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh);
  },
  setUser: (user: User) => {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  },
  clear: () => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.PROVIDER_FILTERS);
  },
};

const isTokenValid = (token: string | null): boolean => {
  if (!token) return false;
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    return exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

const isTokenExpiringSoon = (token: string): boolean => {
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    const timeToExpire = exp * 1000 - Date.now();
    return timeToExpire <= TOKEN_REFRESH_THRESHOLD_MS;
  } catch {
    return true;
  }
};

// ============================================
// Refresh deduplication — prevents concurrent refresh calls
// ============================================

let refreshPromise: Promise<string | undefined> | null = null;

// ============================================
// Store
// ============================================

interface AuthState {
  isLogged: boolean;
  user: User | null;
  isLoading: boolean;
  confirmEmailToken: string | null;
}

interface AuthActions {
  logIn: (credentials: LoginRequest, onRequire2FA: () => void) => Promise<AuthResult>;
  logOut: () => Promise<void>;
  googleLogin: (code: string) => Promise<AuthResult>;
  getAccessToken: () => Promise<string | undefined>;
  getUser: () => User | null;

  register: (data: SignupRequest) => Promise<boolean>;
  resendConfirmationEmail: (token: string) => Promise<'sent' | 'in_progress' | 'error'>;
  confirmEmail: (code: string) => Promise<boolean>;

  requestPasswordReset: (data: ResetPasswordRequest) => Promise<boolean>;
  resetPassword: (data: ResetPasswordConfirmRequest) => Promise<boolean>;
  changePassword: (data: ChangePasswordRequest) => Promise<boolean>;
  fetchUser: () => Promise<User | null>;

  getEffectiveRole: () => EffectiveRole;
  getRole: () => EffectiveRole;
  isAdmin: () => boolean;
  isStaffOrAbove: () => boolean;
  isProviderUser: () => boolean;
  isUnassigned: () => boolean;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()((set, get) => ({
  // State
  isLogged: isTokenValid(storage.getRefreshToken()),
  user: storage.getUser(),
  isLoading: false,
  confirmEmailToken: null,

  // ----------------------------------------
  // Authentication
  // ----------------------------------------

  logIn: async (credentials, onRequire2FA) => {
    // Check rate limit before attempting login
    const rateLimitCheck = authRateLimiter.checkLimit(credentials.email);
    if (!rateLimitCheck.isAllowed) {
      const minutes = Math.ceil((rateLimitCheck.retryAfter || 0) / 60);
      toast.error(
        `Too many login attempts. Please try again in ${minutes} minute${minutes !== 1 ? 's' : ''}.`,
      );
      return 'error';
    }

    set({ isLoading: true });
    toast.dismiss();

    try {
      const { data } = await api.post<LoginResponse>(API_ROUTES.login, credentials);

      // Successful login - reset rate limit
      authRateLimiter.reset(credentials.email);

      storage.setTokens(data.access, data.refresh);
      storage.setUser(data.user);
      set({ isLogged: true, user: data.user, isLoading: false, confirmEmailToken: null });

      return 'success';
    } catch (error: unknown) {
      const apiError = error as { response?: { data?: LoginErrorResponse } };
      // Record failed attempt for rate limiting
      authRateLimiter.recordAttempt(credentials.email);

      const errorData = apiError.response?.data;
      if (!errorData) {
        set({ isLoading: false });
        return 'error';
      }

      // Email not confirmed - resend confirmation
      // Keep loading state active while resending confirmation email
      if (errorData.token?.[0]) {
        set({ confirmEmailToken: errorData.token[0] });
        const sent = await get().resendConfirmationEmail(errorData.token[0]);
        set({ isLoading: false }); // Only stop loading after resend completes
        return sent === 'error' ? 'error' : 'confirm_email';
      }

      // 2FA required
      const errorType = errorData.type?.[0] || errorData.message?.[0];
      if (errorType === 'two_fa_failed') {
        set({ isLoading: false });
        onRequire2FA();
        return 'go2fa';
      }

      // Known error types
      const knownErrors: AuthResult[] = ['wrong_data', 'reset_psw', 'account_block', 'invalid'];
      if (errorType && knownErrors.includes(errorType as AuthResult)) {
        set({ isLoading: false });
        toast.error(AUTH_MESSAGES[errorType as keyof typeof AUTH_MESSAGES] || AUTH_MESSAGES.error);
        return errorType as AuthResult;
      }

      set({ isLoading: false });
      return 'error';
    }
  },

  googleLogin: async (code) => {
    try {
      set({ isLoading: true });
      const { data } = await api.post(API_ROUTES.googleAuth, { code });
      storage.setTokens(data.access, data.refresh);
      storage.setUser(data.user);
      set({ isLogged: true, user: data.user, isLoading: false });
      return 'success';
    } catch (error: unknown) {
      set({ isLoading: false });
      const detail =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error || '';
      if (detail.includes('deactivated')) {
        toast.error('This account has been deactivated');
        return 'account_block';
      }
      toast.error('Google login failed. Please try again.');
      return 'error';
    }
  },

  logOut: async () => {
    set({ isLoading: true });
    toast.dismiss();

    try {
      await api.post(API_ROUTES.logout);
    } catch (error) {
      logger.error('Logout request failed', error);
    }

    storage.clear();
    queryClient.clear();
    set({ isLogged: false, user: null, isLoading: false, confirmEmailToken: null });
  },

  getAccessToken: async () => {
    const accessToken = storage.getAccessToken();
    const refreshToken = storage.getRefreshToken();

    // Token still valid and not expiring soon — return it
    if (accessToken && !isTokenExpiringSoon(accessToken)) {
      return accessToken;
    }

    // Need to refresh (token missing, expired, or expiring soon)
    if (!refreshToken) {
      get().logOut();
      return undefined;
    }

    // Deduplicate: if a refresh is already in-flight, reuse it
    if (refreshPromise) {
      return refreshPromise;
    }

    refreshPromise = (async () => {
      try {
        const { data } = await api.post<TokenResponse>(API_ROUTES.refresh, {
          refresh: refreshToken,
        });

        storage.setTokens(data.access, data.refresh);
        return data.access;
      } catch (error) {
        logger.error('Token refresh failed', error);
        get().logOut();
        return undefined;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  },

  getUser: () => storage.getUser(),

  // ----------------------------------------
  // Registration
  // ----------------------------------------

  register: async (data) => {
    if (data.password1 !== data.password2) {
      toast.error(AUTH_MESSAGES.passwords_mismatch);
      return false;
    }

    set({ isLoading: true });
    toast.dismiss();

    try {
      await api.post(API_ROUTES.signup, data);
      // Don't set confirmEmailToken here - user must login to resend confirmation
      toast.success(AUTH_MESSAGES.register_success);
      return true;
    } catch (error: unknown) {
      const errorData = (
        error as { response?: { data?: { email?: string[]; non_field_errors?: string[] } } }
      ).response?.data;
      if (errorData?.email) toast.error(AUTH_MESSAGES.bad_email);
      else if (errorData?.non_field_errors) toast.error(AUTH_MESSAGES.bad_data);
      else toast.error(AUTH_MESSAGES.error);
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  resendConfirmationEmail: async (token) => {
    // Check rate limit before attempting resend
    const rateLimitCheck = authRateLimiter.checkLimit(`resend:${token}`);
    if (!rateLimitCheck.isAllowed) {
      const minutes = Math.ceil((rateLimitCheck.retryAfter || 0) / 60);
      toast.error(
        `Too many resend attempts. Please try again in ${minutes} minute${minutes !== 1 ? 's' : ''}.`,
      );
      return 'error';
    }

    try {
      const { data } = await api.post(API_ROUTES.resendEmail, { token });
      const status = (data as { Status?: boolean }).Status;
      const code = (data as { code?: string }).code;

      if (status) {
        authRateLimiter.recordAttempt(`resend:${token}`);
        toast.success(AUTH_MESSAGES.resent_code);
        return 'sent';
      }

      if (code === 'Email confirmation in progress') {
        toast.error(
          'A confirmation email was recently sent. Please wait 5 minutes before requesting another one.',
        );
        return 'in_progress';
      }

      authRateLimiter.recordAttempt(`resend:${token}`);
      toast.error(AUTH_MESSAGES.error);
      return 'error';
    } catch (_error: unknown) {
      authRateLimiter.recordAttempt(`resend:${token}`);

      logger.error('Failed to resend confirmation', _error);
      return 'error';
    }
  },

  confirmEmail: async (code) => {
    // Check rate limit before attempting confirmation
    const rateLimitCheck = authRateLimiter.checkLimit(`verify:${code}`);
    if (!rateLimitCheck.isAllowed) {
      const minutes = Math.ceil((rateLimitCheck.retryAfter || 0) / 60);
      toast.error(
        `Too many verification attempts. Please try again in ${minutes} minute${minutes !== 1 ? 's' : ''}.`,
      );
      return false;
    }

    set({ isLoading: true });
    toast.dismiss();

    try {
      const { data } = await api.post<ConfirmEmailResponse>(API_ROUTES.confirmEmail, {
        key: code,
      });

      // Successful verification - reset rate limit
      authRateLimiter.reset(`verify:${code}`);
      set({ confirmEmailToken: null });

      if (data.access && data.refresh) {
        storage.setTokens(data.access, data.refresh);

        if (data.user) {
          storage.setUser(data.user);
          set({ isLogged: true, user: data.user, isLoading: false });
          toast.success(AUTH_MESSAGES.email_confirmed_and_logged_in);
        } else {
          logger.warn(
            'No user data in confirmation response. Setting logged in but user may need to fetch profile.',
          );
          set({ isLogged: true, isLoading: false });
          toast.success(AUTH_MESSAGES.email_confirmed_and_logged_in);
        }
      } else {
        logger.warn('No tokens in confirmation response');
        toast.success(AUTH_MESSAGES.email_confirmed);
      }

      return true;
    } catch (error) {
      // Record failed attempt for rate limiting
      authRateLimiter.recordAttempt(`verify:${code}`);
      logger.error('Email confirmation failed', error);
      toast.error(AUTH_MESSAGES.error);
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  // ----------------------------------------
  // Password Management
  // ----------------------------------------

  requestPasswordReset: async (data) => {
    set({ isLoading: true });
    toast.dismiss();

    try {
      await api.post(API_ROUTES.resetPassword, data);
      toast.success(AUTH_MESSAGES.email_sent);
      return true;
    } catch (error: unknown) {
      if ((error as { response?: { data?: { email?: string[] } } })?.response?.data?.email) {
        toast.error(AUTH_MESSAGES.bad_email);
      }
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  resetPassword: async (data) => {
    set({ isLoading: true });
    toast.dismiss();

    try {
      await api.post(API_ROUTES.resetPasswordConfirm, data);
      toast.success(AUTH_MESSAGES.password_reset_request_success);
      return true;
    } catch (error: unknown) {
      toast.error('An error occurred during login');
      logger.error('Login error:', error);
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  changePassword: async (data) => {
    set({ isLoading: true });
    toast.dismiss();

    try {
      await api.post(API_ROUTES.changePassword, data);
      toast.success(AUTH_MESSAGES.password_reset_success);
      return true;
    } catch (error: unknown) {
      const errorCode = (error as { response?: { data?: { code?: string } } })?.response?.data
        ?.code as keyof typeof AUTH_MESSAGES;
      toast.error(AUTH_MESSAGES[errorCode] || AUTH_MESSAGES.error);
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  fetchUser: async () => {
    try {
      const { data } = await api.get<User>(API_ROUTES.profile);
      storage.setUser(data);
      set({ user: data });
      return data;
    } catch (error) {
      logger.error('Failed to fetch user profile', error);
      return null;
    }
  },

  // ----------------------------------------
  // Role helpers
  // ----------------------------------------

  getEffectiveRole: (): EffectiveRole => {
    const user = get().user ?? storage.getUser();
    if (!user) return 'unassigned';
    if (user.is_superuser) return 'admin';
    if (user.is_staff) return 'staff';
    if (user.role === 'provider') return 'provider';
    return 'unassigned';
  },

  // Alias kept for backwards compatibility
  getRole: () => get().getEffectiveRole(),

  isAdmin: () => get().getEffectiveRole() === 'admin',

  isStaffOrAbove: () => {
    const r = get().getEffectiveRole();
    return r === 'admin' || r === 'staff';
  },

  isProviderUser: () => get().getEffectiveRole() === 'provider',

  isUnassigned: () => get().getEffectiveRole() === 'unassigned',
}));
